import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import type { ReleaseNotesPage, WhatsNew } from "@/api/types";
import { compareSemver } from "@/lib/semver";

export type AppChangelog = {
  source: string;
  markdown: string;
};

/** The engineering log (``CHANGELOG.md``) for the release-notes page's
 *  Technical changelog tab. Global admins only: the endpoint answers
 *  everyone else 403, so the page enables it only for them, and only on
 *  that tab. The file is baked into the backend image and won't change
 *  until the container rolls. */
export function useChangelog({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.changelog,
    queryFn: async () => (await api.get<AppChangelog>("/changelog")).data,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled,
  });
}

export const RELEASE_NOTES_PAGE_SIZE = 20;

/** ``GET /api/release-notes``, one page of releases at a time, newest
 *  first; the next page is the releases before the last one's version. */
export function useReleaseNotes(lang: string) {
  return useInfiniteQuery({
    queryKey: keys.releaseNotes(lang),
    queryFn: async ({ pageParam }) =>
      (
        await api.get<ReleaseNotesPage>("/release-notes", {
          params: { lang, before: pageParam ?? undefined, limit: RELEASE_NOTES_PAGE_SIZE },
        })
      ).data,
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_before,
    // The notes are baked into the image; a deploy reloads the tab.
    staleTime: Infinity,
  });
}

/** ``GET /api/release-notes/whats-new``: the notes this user hasn't
 *  been offered, up to ``through`` (the build this tab runs). The
 *  What's new controller runs it once its gate opens; the navbar reads
 *  the same entry with ``enabled: false`` (no second request). Kept for
 *  the page load: the answer only changes when the user acknowledges,
 *  and that patches the cache (``markWhatsNewSeen``). A language switch
 *  is another key: the text refetches and re-renders in place. */
export function useWhatsNew({
  through,
  lang,
  enabled,
}: {
  through: string | null;
  lang: string;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: keys.whatsNew(through ?? "", lang),
    queryFn: async () =>
      (await api.get<WhatsNew>("/release-notes/whats-new", { params: { through, lang } })).data,
    enabled: enabled && Boolean(through),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    // A new bundle on an older backend gets a 404: nothing to retry.
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** After ``version`` was acknowledged (here or in another tab): every
 *  cached What's new answer up to it has nothing unread any more, which
 *  also clears the navbar dot. */
export function markWhatsNewSeen(qc: QueryClient, version: string, lastSeen?: string | null): void {
  for (const [key, value] of qc.getQueriesData<WhatsNew>({ queryKey: keys.whatsNewAll })) {
    if (!value || compareSemver(value.current_version, version) > 0) continue;
    qc.setQueryData<WhatsNew>(key, {
      ...value,
      first_visit: false,
      notes: [],
      omitted: 0,
      last_seen_version: lastSeen ?? version,
    });
  }
}

/** ``POST /api/release-notes/seen``: the user was offered the notes up
 *  to ``version`` (the server keeps the newest, clamped to its own
 *  version). ``onSeen`` runs after a success even when the caller has
 *  unmounted (the other tabs are told from there). */
export function useMarkReleaseNotesSeen({ onSeen }: { onSeen?: (version: string) => void } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (version: string) =>
      (await api.post<{ last_seen_version: string | null }>("/release-notes/seen", { version })).data,
    onSuccess: (data, version) => {
      markWhatsNewSeen(qc, version, data.last_seen_version);
      onSeen?.(version);
    },
  });
}
